/**
 * Hook useWebSocket
 *
 * Gerencia conexão WebSocket para atualizações em tempo real.
 * Usa um WebSocketProvider para compartilhar uma única conexão
 * entre todos os consumidores, evitando múltiplas conexões de socket
 * e re-renders desnecessários.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './useAuth';

/**
 * Estado da conexão WebSocket
 */
type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * Retorno do hook useWebSocket
 */
interface UseWebSocketReturn {
  /** Socket instance */
  socket: Socket | null;
  /** Estado da conexão */
  connectionState: ConnectionState;
  /** Se está conectado */
  isConnected: boolean;
  /** Conecta ao servidor */
  connect: () => void;
  /** Desconecta do servidor */
  disconnect: () => void;
  /** Emite um evento */
  emit: (event: string, data?: unknown) => void;
  /** Registra um listener de evento */
  on: (event: string, handler: (data: unknown) => void) => void;
  /** Remove um listener de evento */
  off: (event: string, handler?: (data: unknown) => void) => void;
}

/**
 * URL padrão do WebSocket (mesmo servidor da API)
 * Detecta automaticamente se deve usar WSS (HTTPS) ou WS (HTTP)
 */
function getDefaultWsUrl(): string {
  // Se há uma URL configurada, usa ela
  if (import.meta.env.VITE_WS_URL) {
    const configuredUrl = import.meta.env.VITE_WS_URL as string;
    // Se estamos em HTTPS, converte http:// para https:// e ws:// para wss://
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      return configuredUrl
        .replace(/^http:\/\//i, 'https://')
        .replace(/^ws:\/\//i, 'wss://');
    }
    return configuredUrl;
  }

  // URL padrão baseada no protocolo atual
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return 'https://localhost:3333';
  }
  return 'http://localhost:3333';
}

const DEFAULT_WS_URL = getDefaultWsUrl();

/**
 * Contexto do WebSocket compartilhado
 */
const WebSocketContext = createContext<UseWebSocketReturn | null>(null);

/**
 * Provider que gerencia uma única conexão WebSocket compartilhada.
 *
 * Deve ser colocado dentro do AuthProvider para ter acesso ao estado
 * de autenticação. Todos os consumidores (useWebSocket, useSocketEvent)
 * compartilham a mesma conexão, eliminando múltiplas conexões de socket.
 *
 * @example
 * <AuthProvider>
 *   <WebSocketProvider>
 *     <App />
 *   </WebSocketProvider>
 * </AuthProvider>
 */
export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');

  // Ref para user — evita que connect seja recriado quando a referência
  // do user muda (ex: resposta duplicada do /auth/me no StrictMode)
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  /**
   * Cria e configura a conexão Socket
   */
  const connect = useCallback(() => {
    if (!isAuthenticated) return;
    if (socketRef.current?.connected) return;

    setConnectionState('connecting');

    const token = localStorage.getItem('vendeai_access_token');
    const currentUser = userRef.current;

    const socket = io(DEFAULT_WS_URL, {
      transports: ['websocket', 'polling'],
      auth: {
        token,
        userId: currentUser?.id,
        tenantId: currentUser?.tenantId,
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on('connect', () => setConnectionState('connected'));
    socket.on('disconnect', () => setConnectionState('disconnected'));
    socket.on('connect_error', () => setConnectionState('error'));

    socketRef.current = socket;
  }, [isAuthenticated]);

  /**
   * Desconecta do servidor
   */
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setConnectionState('disconnected');
    }
  }, []);

  /**
   * Emite um evento
   */
  const emit = useCallback((event: string, data?: unknown) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  }, []);

  /**
   * Registra um listener de evento
   */
  const on = useCallback((event: string, handler: (data: unknown) => void) => {
    if (socketRef.current) {
      socketRef.current.on(event, handler);
    }
  }, []);

  /**
   * Remove um listener de evento
   */
  const off = useCallback((event: string, handler?: (data: unknown) => void) => {
    if (socketRef.current) {
      if (handler) {
        socketRef.current.off(event, handler);
      } else {
        socketRef.current.off(event);
      }
    }
  }, []);

  // Auto-conecta quando autenticado
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    }
    return () => { disconnect(); };
  }, [isAuthenticated, connect, disconnect]);

  const value = useMemo<UseWebSocketReturn>(() => ({
    socket: socketRef.current,
    connectionState,
    isConnected: connectionState === 'connected',
    connect,
    disconnect,
    emit,
    on,
    off,
  }), [connectionState, connect, disconnect, emit, on, off]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook para acessar a conexão WebSocket compartilhada
 *
 * @returns Estado e funções do WebSocket
 *
 * @example
 * const { isConnected, emit, on } = useWebSocket();
 *
 * // Escutar eventos
 * useEffect(() => {
 *   on('order:created', (order) => {
 *     console.log('Novo pedido:', order);
 *   });
 * }, [on]);
 *
 * // Emitir eventos
 * emit('order:status-update', { orderId: '123', status: 'PREPARING' });
 */
export function useWebSocket(): UseWebSocketReturn {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
}

/**
 * Hook simplificado para escutar um evento específico
 *
 * Usa um ref para o handler para evitar re-registros desnecessários
 * do listener no socket quando o handler muda (ex: quando suas
 * dependências como fetchOrders mudam). Isso elimina churn de
 * subscribe/unsubscribe e possíveis race conditions.
 *
 * @param event - Nome do evento
 * @param handler - Callback quando o evento ocorrer
 *
 * @example
 * useSocketEvent('order:created', (order) => {
 *   toast.info(`Novo pedido #${order.number}`);
 *   refetchOrders();
 * });
 */
export function useSocketEvent<T = unknown>(
  event: string,
  handler: (data: T) => void,
): void {
  const { on, off, isConnected } = useWebSocket();

  // Ref para o handler — sempre aponta para a versão mais recente
  // sem causar re-registro do listener no socket
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);

  useEffect(() => {
    if (!isConnected) return;

    // Wrapper estável que delega para o handler atual via ref
    const stableHandler = (data: unknown) => handlerRef.current(data as T);
    on(event, stableHandler);

    return () => {
      off(event, stableHandler);
    };
  }, [event, on, off, isConnected]);
}

export default useWebSocket;
